import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  Zap, Heart, ThumbsDown, Star, MessageCircle,
  Play, Square, Settings, Activity, AlertCircle, Scan,
  Smartphone, Flame
} from 'lucide-react';
import AutomationBanner from '../components/AutomationBanner';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const TINDER = '#FE3C72';
const TINDER_DARK = '#E0355F';

const slideIn = keyframes`from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); }`;

const Page = styled.div`width: 100%;`;

const ContentGrid = styled.div`
  display: grid; grid-template-columns: 1fr 380px; gap: 20px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border);
  border-radius: 14px; overflow: hidden;
`;

const CardHead = styled.div`
  padding: 14px 18px; border-bottom: 1px solid var(--border);
  display: flex; align-items: center; gap: 10px; font-weight: 700; font-size: 14px;
  color: var(--text-primary);
  svg { color: ${p => p.$color || TINDER}; }
`;

const AccountRow = styled.div`
  display: flex; align-items: center; gap: 14px; padding: 14px 18px;
  border-bottom: 1px solid var(--border);
`;

const AccountPic = styled.div`
  width: 48px; height: 48px; border-radius: 50%;
  background: linear-gradient(135deg, ${TINDER}, ${TINDER_DARK});
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 800; font-size: 18px;
`;

const AccountInfo = styled.div`
  flex: 1;
  .name { font-weight: 700; font-size: 14px; color: var(--text-primary); }
  .handle { font-size: 12px; color: var(--text-muted); font-family: monospace; }
`;

const StatsGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; padding: 10px 18px 14px;
`;

const StatBadge = styled.div`
  display: flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 6px;
  background: ${p => p.$color}12; font-size: 11px; font-weight: 700;
  svg { color: ${p => p.$color}; }
  .val { color: ${p => p.$color}; font-family: 'JetBrains Mono', monospace; }
  .lbl { color: var(--text-muted); font-size: 9px; font-weight: 600; }
`;

/* ── Script Tabs ── */
const TabRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; padding: 14px 18px;
  border-bottom: 1px solid var(--border);
`;

const TabBtn = styled.button`
  display: flex; align-items: center; gap: 5px;
  padding: 6px 14px; border-radius: 8px; border: none; cursor: pointer;
  font-size: 12px; font-weight: 700; transition: all .15s;
  background: ${p => p.$act ? `${p.$color || TINDER}` : 'var(--surface)'};
  color: ${p => p.$act ? '#fff' : 'var(--text-muted)'};
  &:hover { opacity: .9; transform: translateY(-1px); }
`;

/* ── Launch Button ── */
const LaunchBtn = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 24px; border-radius: 10px; border: none; cursor: pointer;
  font-size: 13px; font-weight: 700; color: #fff; transition: all .2s;
  background: ${p => p.$stop ? '#ef4444' : `linear-gradient(135deg, ${TINDER}, ${TINDER_DARK})`};
  &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px ${p => p.$stop ? 'rgba(239,68,68,.3)' : 'rgba(254,60,114,.3)'}; }
  &:disabled { opacity: .5; cursor: not-allowed; transform: none; }
`;

/* ── Params ── */
const ParamsSection = styled.div`padding: 14px 18px;`;
const ParamRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 8px 0;
`;
const ParamLabel = styled.span`font-size: 12px; font-weight: 600; color: var(--text-primary);`;
const ParamValue = styled.span`font-size: 12px; font-weight: 700; color: ${TINDER}; font-family: 'JetBrains Mono', monospace;`;

const ParamSlider = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none; width: 100%; height: 4px; border-radius: 2px;
  background: var(--border); outline: none; margin-bottom: 12px;
  &::-webkit-slider-thumb { -webkit-appearance: none; width: 14px; height: 14px; border-radius: 50%;
    background: ${TINDER}; cursor: pointer; }
`;

/* ── Actions Feed ── */
const ActionsList = styled.div`
  max-height: 500px; overflow-y: auto; padding: 0 14px 14px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const ActionItem = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 8px 6px;
  border-bottom: 1px solid var(--border);
  animation: ${slideIn} .3s ease;
  &:last-child { border-bottom: none; }
`;

const ActionDot = styled.div`
  width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
  background: ${p => p.$color}15; display: flex; align-items: center; justify-content: center;
  svg { color: ${p => p.$color}; }
`;

const ActionText = styled.div`
  flex: 1; min-width: 0;
  .user { font-size: 12px; font-weight: 700; color: var(--text-primary);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .type { font-size: 10px; color: var(--text-muted); }
`;

const ActionTime = styled.span`font-size: 10px; color: var(--text-muted); white-space: nowrap;`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 40px 20px; color: var(--text-muted);
  svg { margin-bottom: 8px; opacity: .4; }
  span { font-size: 13px; }
`;

/* ═══════════════════════════════════════════════
   ACTION TYPE HELPERS
   ═══════════════════════════════════════════════ */

const ACTION_META = {
  like:       { icon: Heart, color: '#22c55e', label: 'Like' },
  nope:       { icon: ThumbsDown, color: '#ef4444', label: 'Nope' },
  super_like: { icon: Star, color: '#3b82f6', label: 'Super Like' },
  message:    { icon: MessageCircle, color: '#a855f7', label: 'Message' },
  scrape:     { icon: Scan, color: '#06b6d4', label: 'Scrape' },
  match:      { icon: Flame, color: TINDER, label: 'Match!' },
};

const getActionMeta = (type) => ACTION_META[type] || { icon: Activity, color: '#6b7280', label: type };

const fmtDuration = (start) => {
  const ms = Date.now() - new Date(start).getTime();
  const s = Math.floor(ms / 1000); const m = Math.floor(s / 60); const h = Math.floor(m / 60);
  return `${String(h).padStart(2,'0')}:${String(m%60).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;
};

const fmtAgo = (ts) => {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s/60)}m`;
  return `${Math.floor(s/3600)}h`;
};

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const TinderAutomation = ({ selectedDevice, onNavigate }) => {
  const deviceId = selectedDevice?.device_id;

  // State
  const [account, setAccount] = useState(null);
  const [actions, setActions] = useState([]);
  const [todaySessions, setTodaySessions] = useState([]);
  const [processInfo, setProcessInfo] = useState(null);
  const [timer, setTimer] = useState('00:00:00');
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState(null);

  // Script params
  const [scriptType, setScriptType] = useState('swipe');
  const [likeRatio, setLikeRatio] = useState(50);
  const [sessionMaxMins, setSessionMaxMins] = useState(45);
  const [maxActions, setMaxActions] = useState(100);
  const [speedLevel, setSpeedLevel] = useState(2);
  const [profileTapsMin, setProfileTapsMin] = useState(0);
  const [profileTapsMax, setProfileTapsMax] = useState(3);
  const [tapPauseMin, setTapPauseMin] = useState(0.5);
  const [tapPauseMax, setTapPauseMax] = useState(2.0);
  const [actionDelayMin, setActionDelayMin] = useState(3.0);
  const [actionDelayMax, setActionDelayMax] = useState(8.0);
  const [openTerminal, setOpenTerminal] = useState(true);
  const [paramsOpen, setParamsOpen] = useState(true);

  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const stoppedAtRef = useRef(0);

  // ── Data Fetching ──

  const fetchAccount = useCallback(async () => {
    if (!deviceId) return;
    try {
      const r = await fetch(`/api/tinder-scripts/account/${deviceId}`);
      const d = await r.json();
      setAccount(d.account || null);
      if (d.account) {
        setLikeRatio(Math.round((d.account.like_ratio || 0.5) * 100));
        setSessionMaxMins(d.account.session_max_mins || 45);
        setMaxActions(d.account.session_max_actions || 100);
        setSpeedLevel(d.account.speed_level || 2);
        setProfileTapsMin(d.account.profile_taps_min ?? 0);
        setProfileTapsMax(d.account.profile_taps_max ?? 3);
        setTapPauseMin(d.account.tap_pause_min ?? 0.5);
        setTapPauseMax(d.account.tap_pause_max ?? 2.0);
        setActionDelayMin(d.account.action_delay_min ?? 3.0);
        setActionDelayMax(d.account.action_delay_max ?? 8.0);
      }
    } catch (_) {}
  }, [deviceId]);

  const fetchActions = useCallback(async () => {
    if (!account?.id) return;
    try {
      const r = await fetch(`/api/tinder-scripts/live-actions/${account.id}?limit=80`);
      const d = await r.json();
      setActions(d.actions || []);
    } catch (_) {}
  }, [account?.id]);

  const fetchProcess = useCallback(async () => {
    if (!account?.id) return;
    if (Date.now() - stoppedAtRef.current < 10000) return;
    try {
      const r = await fetch(`/api/tinder-scripts/status/${account.id}`);
      const d = await r.json();
      setProcessInfo(d.isRunning ? d : null);
    } catch (_) { setProcessInfo(null); }
  }, [account?.id]);

  const fetchTodaySessions = useCallback(async () => {
    if (!account?.id) return;
    try {
      const r = await fetch(`/api/tinder-scripts/today-sessions/${account.id}`);
      const d = await r.json();
      setTodaySessions(d.sessions || []);
    } catch (_) {}
  }, [account?.id]);

  // Initial load + polling
  useEffect(() => { fetchAccount(); }, [fetchAccount]);

  useEffect(() => {
    if (!account?.id) return;
    fetchActions(); fetchProcess(); fetchTodaySessions();
    pollRef.current = setInterval(() => { fetchActions(); fetchProcess(); fetchTodaySessions(); }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [account?.id, fetchActions, fetchProcess, fetchTodaySessions]);

  // Timer
  useEffect(() => {
    const start = processInfo?.startedAt || processInfo?.started_at;
    if (processInfo?.isRunning && start) {
      const tick = () => setTimer(fmtDuration(start));
      tick();
      timerRef.current = setInterval(tick, 1000);
    } else { setTimer('00:00:00'); }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [processInfo]);

  // ── Computed helpers ──
  const canLaunch = !launching && !!deviceId;

  // ── Launch / Stop ──

  const handleLaunch = async () => {
    if (launching || !deviceId) return;
    setLaunching(true); setLaunchError(null);
    try {
      const body = {
        device_id: deviceId,
        script_type: scriptType,
        account_name: account?.account_name || '',
        like_ratio: likeRatio / 100,
        session_max_mins: sessionMaxMins,
        session_max_actions: maxActions,
        speed_level: speedLevel,
        profile_taps_min: profileTapsMin,
        profile_taps_max: profileTapsMax,
        tap_pause_min: tapPauseMin,
        tap_pause_max: tapPauseMax,
        action_delay_min: actionDelayMin,
        action_delay_max: actionDelayMax,
        open_terminal: openTerminal,
      };
      const r = await fetch('/api/tinder-scripts/start-interaction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok || data.error) {
        setLaunchError(data.error || `Erreur ${r.status}`);
      } else {
        setProcessInfo({
          isRunning: true, scriptType,
          startedAt: new Date().toISOString(),
        });
        setTimeout(() => { fetchProcess(); }, 5000);
      }
    } catch (e) { setLaunchError(e.message); }
    setLaunching(false);
  };

  const handleStop = async () => {
    stoppedAtRef.current = Date.now();
    setProcessInfo(null);
    try {
      await fetch('/api/tinder-scripts/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId }),
      });
    } catch (_) {}
  };

  // ── Computed ──
  const isRunning = processInfo?.isRunning;
  const accountName = account?.account_name || '';

  // Today's session stats aggregated
  const sessionCounts = (() => {
    const totals = { likes: 0, nopes: 0, super_likes: 0, matches: 0, messages: 0 };
    todaySessions.forEach(s => {
      totals.likes += parseInt(s.total_likes) || 0;
      totals.nopes += parseInt(s.total_nopes) || 0;
      totals.super_likes += parseInt(s.total_super_likes) || 0;
      totals.matches += parseInt(s.total_matches) || 0;
      totals.messages += parseInt(s.total_messages_sent) || 0;
    });
    // Fallback: count from actions if no session data
    if (Object.values(totals).every(v => v === 0) && actions.length > 0) {
      actions.forEach(a => {
        if (a.type === 'like') totals.likes++;
        else if (a.type === 'nope') totals.nopes++;
        else if (a.type === 'super_like') totals.super_likes++;
        else if (a.type === 'message') totals.messages++;
      });
    }
    return totals;
  })();

  // ── Render ──
  if (!deviceId) return <Page><EmptyState><Smartphone size={32} /><span>Sélectionnez un appareil</span></EmptyState></Page>;

  return (
    <Page>
      {/* ── Top Banner ── */}
      <AutomationBanner
        platform="tinder"
        deviceName={deviceId}
        isConnected={!isRunning}
        isRunning={isRunning}
        timer={isRunning ? timer : null}
        onNavigate={onNavigate}
      />

      <ContentGrid>
        {/* ═══════ LEFT COLUMN ═══════ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Account Info Card */}
          <Card>
            <AccountRow>
              <AccountPic>
                {account?.profile_pic_url
                  ? <img src={`/api/tinder-scripts/profile-photo/${deviceId}?t=${Date.now()}`}
                         alt={accountName}
                         style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                         onError={e => { e.target.style.display = 'none'; }}
                    />
                  : (accountName[0] || '?').toUpperCase()
                }
              </AccountPic>
              <AccountInfo>
                <div className="name">{accountName || '—'}</div>
                <div className="handle">{deviceId}</div>
              </AccountInfo>
            </AccountRow>

            {/* Session Stats Badges */}
            <StatsGrid>
              {[
                { icon: Heart, color: '#22c55e', val: sessionCounts.likes, label: 'LIKES' },
                { icon: ThumbsDown, color: '#ef4444', val: sessionCounts.nopes, label: 'NOPES' },
                { icon: Star, color: '#3b82f6', val: sessionCounts.super_likes, label: 'SUPER LIKES' },
                { icon: Flame, color: TINDER, val: sessionCounts.matches, label: 'MATCHES' },
                { icon: MessageCircle, color: '#a855f7', val: sessionCounts.messages, label: 'MESSAGES' },
              ].filter(s => s.val > 0).map(s => (
                <StatBadge key={s.label} $color={s.color}>
                  <s.icon size={10} />
                  <span className="val">{s.val}</span>
                  <span className="lbl">{s.label}</span>
                </StatBadge>
              ))}
            </StatsGrid>

            {/* Script Type Tabs */}
            <TabRow>
              {[
                { id: 'swipe', label: 'Swipe', icon: Heart, color: '#22c55e' },
                { id: 'swipe-like-all', label: 'Like All', icon: Zap, color: TINDER },
                { id: 'message', label: 'Messages', icon: MessageCircle, color: '#a855f7' },
                { id: 'scrape', label: 'Scrape', icon: Scan, color: '#06b6d4' },
              ].map(t => (
                <TabBtn key={t.id} $act={scriptType === t.id} $color={t.color}
                  onClick={() => setScriptType(t.id)}>
                  <t.icon size={13} /> {t.label}
                </TabBtn>
              ))}
            </TabRow>

            {/* Params Section */}
            <ParamsSection>
              <ParamRow style={{ cursor: 'pointer' }} onClick={() => setParamsOpen(p => !p)}>
                <ParamLabel style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Settings size={13} /> Paramètres
                </ParamLabel>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{paramsOpen ? '▲' : '▼'}</span>
              </ParamRow>

              {paramsOpen && (
                <>
                  {(scriptType === 'swipe') && (
                    <>
                      <ParamRow>
                        <ParamLabel>Ratio de like</ParamLabel>
                        <ParamValue>{likeRatio}%</ParamValue>
                      </ParamRow>
                      <ParamSlider min="10" max="90" value={likeRatio}
                        onChange={e => setLikeRatio(parseInt(e.target.value))} />
                    </>
                  )}

                  <ParamRow>
                    <ParamLabel>Durée max (min)</ParamLabel>
                    <ParamValue>{sessionMaxMins}</ParamValue>
                  </ParamRow>
                  <ParamSlider min="5" max="120" value={sessionMaxMins}
                    onChange={e => setSessionMaxMins(parseInt(e.target.value))} />

                  <ParamRow>
                    <ParamLabel>Max actions</ParamLabel>
                    <ParamValue>{maxActions}</ParamValue>
                  </ParamRow>
                  <ParamSlider min="10" max="300" value={maxActions}
                    onChange={e => setMaxActions(parseInt(e.target.value))} />

                  <ParamRow>
                    <ParamLabel>Vitesse</ParamLabel>
                    <ParamValue>{speedLevel}</ParamValue>
                  </ParamRow>
                  <ParamSlider min="1" max="5" value={speedLevel}
                    onChange={e => setSpeedLevel(parseInt(e.target.value))} />

                  {(scriptType === 'swipe' || scriptType === 'swipe-like-all') && (
                    <>
                      {/* ── Profile Browsing (taps on photos) ── */}
                      <div style={{ marginTop: 8, marginBottom: 4, fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Navigation profil
                      </div>

                      <ParamRow>
                        <ParamLabel>Taps min sur profil</ParamLabel>
                        <ParamValue>{profileTapsMin}</ParamValue>
                      </ParamRow>
                      <ParamSlider min="0" max="5" value={profileTapsMin}
                        onChange={e => {
                          const v = parseInt(e.target.value);
                          setProfileTapsMin(v);
                          if (v > profileTapsMax) setProfileTapsMax(v);
                        }} />

                      <ParamRow>
                        <ParamLabel>Taps max sur profil</ParamLabel>
                        <ParamValue>{profileTapsMax}</ParamValue>
                      </ParamRow>
                      <ParamSlider min="0" max="5" value={profileTapsMax}
                        onChange={e => {
                          const v = parseInt(e.target.value);
                          setProfileTapsMax(v);
                          if (v < profileTapsMin) setProfileTapsMin(v);
                        }} />

                      <ParamRow>
                        <ParamLabel>Pause entre taps (min)</ParamLabel>
                        <ParamValue>{tapPauseMin.toFixed(1)}s</ParamValue>
                      </ParamRow>
                      <ParamSlider min="0.3" max="5" step="0.1" value={tapPauseMin}
                        onChange={e => {
                          const v = parseFloat(e.target.value);
                          setTapPauseMin(v);
                          if (v > tapPauseMax) setTapPauseMax(v);
                        }} />

                      <ParamRow>
                        <ParamLabel>Pause entre taps (max)</ParamLabel>
                        <ParamValue>{tapPauseMax.toFixed(1)}s</ParamValue>
                      </ParamRow>
                      <ParamSlider min="0.5" max="8" step="0.1" value={tapPauseMax}
                        onChange={e => {
                          const v = parseFloat(e.target.value);
                          setTapPauseMax(v);
                          if (v < tapPauseMin) setTapPauseMin(v);
                        }} />

                      {/* ── Delay Between Actions ── */}
                      <div style={{ marginTop: 8, marginBottom: 4, fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Délai entre actions
                      </div>

                      <ParamRow>
                        <ParamLabel>Délai min entre swipes</ParamLabel>
                        <ParamValue>{actionDelayMin.toFixed(1)}s</ParamValue>
                      </ParamRow>
                      <ParamSlider min="0.5" max="20" step="0.5" value={actionDelayMin}
                        onChange={e => {
                          const v = parseFloat(e.target.value);
                          setActionDelayMin(v);
                          if (v > actionDelayMax) setActionDelayMax(v);
                        }} />

                      <ParamRow>
                        <ParamLabel>Délai max entre swipes</ParamLabel>
                        <ParamValue>{actionDelayMax.toFixed(1)}s</ParamValue>
                      </ParamRow>
                      <ParamSlider min="1" max="30" step="0.5" value={actionDelayMax}
                        onChange={e => {
                          const v = parseFloat(e.target.value);
                          setActionDelayMax(v);
                          if (v < actionDelayMin) setActionDelayMin(v);
                        }} />
                    </>
                  )}
                </>
              )}
            </ParamsSection>

            {/* Launch / Stop */}
            <div style={{ padding: '0 18px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              {!isRunning ? (
                <LaunchBtn onClick={handleLaunch} disabled={!canLaunch}>
                  <Play size={14} /> {launching ? 'Lancement…' : 'Lancer'}
                </LaunchBtn>
              ) : (
                <LaunchBtn $stop onClick={handleStop}>
                  <Square size={14} /> Arrêter
                </LaunchBtn>
              )}
            </div>

            {/* Terminal checkbox */}
            <div style={{ padding: '0 18px 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={openTerminal} onChange={e => setOpenTerminal(e.target.checked)} />
              <span style={{ color: 'var(--text-muted)' }}>Ouvrir un terminal pour les logs du script</span>
            </div>

            {launchError && (
              <div style={{ padding: '0 18px 14px', fontSize: 12, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={14} /> {launchError}
              </div>
            )}
          </Card>
        </div>

        {/* ═══════ RIGHT COLUMN — Actions Feed ═══════ */}
        <Card>
          <CardHead $color={TINDER}>
            <Activity size={16} /> Actions récentes
          </CardHead>

          {actions.length === 0 ? (
            <EmptyState>
              <Heart size={24} />
              <span>Aucune action récente</span>
            </EmptyState>
          ) : (
            <ActionsList>
              {actions.map((a, i) => {
                const meta = getActionMeta(a.type);
                const Icon = meta.icon;
                return (
                  <ActionItem key={a.id || i}>
                    <ActionDot $color={meta.color}><Icon size={13} /></ActionDot>
                    <ActionText>
                      <div className="user">{a.target_username || '—'}</div>
                      <div className="type">{meta.label}{a.matched ? ' — Match!' : ''}</div>
                    </ActionText>
                    {a.timestamp && <ActionTime>{fmtAgo(a.timestamp)}</ActionTime>}
                  </ActionItem>
                );
              })}
            </ActionsList>
          )}
        </Card>
      </ContentGrid>
    </Page>
  );
};

export default TinderAutomation;
